import { useState } from 'react';
import { FileDashed, Palette, SquaresFour, Warning } from '@phosphor-icons/react';

import type { LinkedChild, Role } from '../../hooks/use-roles';
import { SideItem, type RailIcon } from './side-item';
import { activeRailPath } from './rail-active';
import { RailChildSwitcher } from './rail-child-switcher';

export interface RailEntry {
  readonly to: string;
  readonly icon: RailIcon;
  readonly label: string;
  readonly accent?: string;
}

export interface RailContext {
  readonly isAdmin: boolean;
  readonly isTeacher: boolean;
  readonly isSchoolDirector: boolean;
  readonly activeRole: Role | null;
  readonly linkedChildren: LinkedChild[];
  readonly activeStudentId: string | null;
}

const BLUE = 'var(--naia-blue)';

/*
 * Les entrées sont DÉCRITES, pas écrites en JSX. C'est ce qui permet de calculer
 * l'entrée active à l'échelle du rail : le rendu et la liste des chemins ne
 * peuvent plus diverger, puisqu'ils sortent de la même source.
 */
export function railEntries(ctx: RailContext): RailEntry[] {
  const { isAdmin, isTeacher, isSchoolDirector, activeRole, linkedChildren, activeStudentId } = ctx;

  if (isAdmin) {
    return [
      { to: '/admin', icon: 'home', label: 'Tableau de bord', accent: BLUE },
      { to: '/admin/safeguarding', icon: Warning, label: 'Alertes', accent: BLUE },
      { to: '/admin/conversations', icon: 'chat', label: 'Conversations', accent: BLUE },
      { to: '/admin/chat', icon: 'lab', label: "Labo d'outils", accent: BLUE },
      { to: '/admin/prompts', icon: 'prompts', label: 'Prompts système', accent: BLUE },
      { to: '/admin/docs', icon: 'documents', label: 'Documents', accent: BLUE },
      { to: '/admin/curriculum', icon: 'curriculum', label: 'Curriculum', accent: BLUE },
      { to: '/admin/curriculum/ref', icon: 'curriculum', label: 'Référentiel', accent: BLUE },
      { to: '/admin/couverture', icon: SquaresFour, label: 'Couverture du corpus', accent: BLUE },
      { to: '/admin/programmes-manquants', icon: FileDashed, label: 'Programmes manquants', accent: BLUE },
      { to: '/admin/items', icon: 'search', label: 'Recherche items', accent: BLUE },
      { to: '/admin/contenus', icon: 'resources', label: 'Contenus', accent: BLUE },
      { to: '/admin/users', icon: 'teacher', label: 'Utilisateurs & rôles', accent: BLUE },
      { to: '/admin/users/schools', icon: 'settings', label: 'Établissements', accent: BLUE },
      { to: '/admin/llm-usage', icon: 'admin', label: 'Usage LLM', accent: BLUE },
      { to: '/design-system', icon: Palette, label: 'Design system', accent: BLUE },
    ];
  }

  if (isTeacher) {
    return [
      { to: '/teacher', icon: 'home', label: 'Tableau de bord' },
      { to: '/teacher/chat', icon: 'chat', label: 'Assistant programme' },
      { to: '/conversations', icon: 'chat', label: 'Mes conversations' },
      { to: '/teacher/library', icon: 'documents', label: 'Ma bibliothèque' },
      { to: '/teacher/assignments', icon: 'parcours', label: 'Mes devoirs' },
      { to: '/teacher/exams', icon: 'documents', label: 'Examens sur table' },
      { to: '/mini-apps', icon: 'parcours', label: 'Mini-apps' },
    ];
  }

  if (activeRole === 'ai_operator') {
    return [
      { to: '/admin/conversations', icon: 'chat', label: 'Conversations' },
      { to: '/admin/safeguarding', icon: Warning, label: 'Alertes' },
      { to: '/admin/llm-usage', icon: 'admin', label: 'Usage LLM' },
      { to: '/admin/curriculum', icon: 'curriculum', label: 'Curriculum' },
      { to: '/admin/prompts', icon: 'prompts', label: 'Prompts système' },
    ];
  }

  if (activeRole === 'direction') {
    return [
      { to: '/direction', icon: 'admin', label: 'Tableau de bord' },
      { to: '/direction/supervision', icon: 'parcours', label: 'Supervision' },
      { to: '/mini-apps', icon: 'parcours', label: 'Mini-apps' },
      ...(isSchoolDirector
        ? ([
            { to: '/direction/enseignants', icon: 'teacher', label: 'Enseignants' },
            { to: '/direction/contenus', icon: 'documents', label: 'Contenus' },
            { to: '/direction/groupes', icon: 'cours', label: 'Groupes de classes' },
            { to: '/direction/equipe', icon: 'settings', label: "Équipe de direction" },
            { to: '/direction/parents-eleves', icon: 'student', label: 'Parents & Élèves' },
          ] as RailEntry[])
        : ([{ to: '/direction/mes-classes', icon: 'cours', label: 'Mes classes' }] as RailEntry[])),
    ];
  }

  if (activeRole === 'tutor') {
    const child = linkedChildren.find((c) => c.classroomStudentId === activeStudentId) ?? null;
    const base: RailEntry[] = [{ to: '/tutor', icon: 'student', label: 'Mes enfants' }];
    if (!activeStudentId) return base;
    base.push({ to: `/tutor/enfant/${activeStudentId}`, icon: 'parcours', label: 'Devoirs' });
    /* Le chat s'active enfant par enfant : proposer l'entrée pour un enfant sans
       activation mènerait à une page verrouillée. */
    if (child?.studentChatEnabled) {
      base.push({ to: `/tutor/enfant/${activeStudentId}/chat`, icon: 'chat', label: 'Discuter avec Naïa' });
      base.push({
        to: `/tutor/enfant/${activeStudentId}/conversations`,
        icon: 'chat',
        label: 'Mes conversations',
      });
    }
    return base;
  }

  return [
    { to: '/app', icon: 'home', label: 'Mon espace' },
    { to: '/student/assignments', icon: 'parcours', label: 'Mes devoirs' },
    { to: '/mini-apps/parcours', icon: 'parcours', label: 'Mes parcours' },
    { to: '/mini-apps/competences', icon: 'curriculum', label: 'Mes compétences' },
    { to: '/conversations', icon: 'chat', label: 'Mes conversations' },
    { to: '/mini-apps', icon: 'parcours', label: 'Mini-apps' },
  ];
}

/** Chemins rendus par le rail, `/settings` et `/aide` compris — ils vivent hors de RailNav. */
export function railPaths(ctx: RailContext): string[] {
  return [...railEntries(ctx).map((e) => e.to), '/settings', '/aide'];
}

/**
 * Role-based navigation links of the side rail. Purely presentational — the
 * active role + current path come from the parent (Header), which keeps all the
 * rail state.
 */
export function RailNav({
  isAdmin,
  isTeacher,
  isSchoolDirector,
  activeRole,
  current,
  onNav,
  linkedChildren,
  activeStudentId,
  onSelectChild,
}: {
  isAdmin: boolean;
  isTeacher: boolean;
  isSchoolDirector: boolean;
  activeRole: Role | null;
  current: string;
  onNav: () => void;
  linkedChildren: LinkedChild[];
  activeStudentId: string | null;
  onSelectChild: (id: string) => void;
}) {
  const ctx = { isAdmin, isTeacher, isSchoolDirector, activeRole, linkedChildren, activeStudentId };
  const [childMenuOpen, setChildMenuOpen] = useState(false);
  /* Une seule entrée active, décidée ici et non par chaque entrée : la plus
     longue qui couvre le chemin courant. `/settings` participe au calcul bien
     qu'il soit rendu par le parent. */
  const active = activeRailPath(railPaths(ctx), current);

  return (
    <div className="flex flex-col gap-px">
      {activeRole === 'tutor' && (
        <RailChildSwitcher
          linkedChildren={linkedChildren}
          activeStudentId={activeStudentId}
          open={childMenuOpen}
          setOpen={setChildMenuOpen}
          onSwitch={onSelectChild}
        />
      )}
      {railEntries(ctx).map((e) => (
        <SideItem
          key={e.to}
          to={e.to}
          icon={e.icon}
          current={current}
          accent={e.accent}
          active={e.to === active}
          onNav={onNav}
        >
          {e.label}
        </SideItem>
      ))}
    </div>
  );
}
